import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-hot-toast";
import { FiSettings } from "react-icons/fi";
import { useBranding } from "../../hooks/useBranding";
import { useModerationQueue } from "../../hooks/useAdmin";
import { updateUserInfo } from "../../app/slices/userSlice";
import { apiUrl } from "../../utils/backendUrl";
import { buildTenantRelativePath } from "../../utils/tenantPreview";
import ClanWorkspaceShell from "../../components/layout/ClanWorkspaceShell";
import InviteDialog from "../../components/shared/InviteDialog";
import WorkspacePeopleRail from "../../components/shared/WorkspacePeopleRail";
import AdminSettingsTabs, {
  ADMIN_SETTINGS_TABS,
  normalizeAdminSettingsTabKey,
} from "./AdminSettingsTabs";
import {
  isAdminNavItemActive,
  isCalibrationAdminRoute,
  isTenantAdminRoute,
  normalizeAdminPath,
} from "./AdminLayoutRouteContracts";
import styles from "./AdminLayout.module.css";
import shellStyles from "./AdminShell.module.css";

// Grouped nav, copy, and route targets extracted directly from the
// prototype's Admin section (left rail: Administration / <clan>, grouped
// OVERVIEW/PEOPLE/CONTENT & TRUST/MESSAGING/GOVERNANCE/RECORDS/CONFIGURATION
// nav). Routes reuse the existing pages wired in pages/admin/index.jsx —
// no new routes were invented:
//   Dashboard -> Home, Users -> Members, Subclans -> Structure,
//   ModerationQueue -> Moderation, Events -> Events,
//   Notifications -> Notifications, AuditTrail -> Audit trail,
//   Settings -> Settings, Calibration -> the same four calibration pages,
//   now folded into this shell's own Outlet at
//   /administrator/calibration/* (router.jsx's calibrationFoldInRoutes;
//   copy shortened to match the prototype).
// Stage 3 of the calibration fold-in (.agent/clan-admin-parity-execplan.md
// §2 point 3) retargeted the Calibration entry below from the legacy
// standalone /admin surface to the in-shell route above. The legacy
// surface still exists and still works — Stage 4 retires it, not this —
// it is just no longer where this nav item points. See
// AdminLayoutRouteContracts.js's isTenantAdminRoute for the matching
// allowlist fix this retarget depends on.
// Platform admins and tenant admins see the identical list (the two were
// already byte-identical arrays before this redesign).
const ADMIN_SECTIONS = [
  { label: "Home", to: "/administrator", exact: true, group: "Overview" },
  {
    label: "Members",
    to: "/administrator/users",
    activePaths: ["/administrator/users"],
    group: "People",
  },
  { label: "Structure", to: "/administrator/subclans", group: "People" },
  { label: "Moderation", to: "/administrator/moderation", group: "Content & trust" },
  { label: "Events", to: "/administrator/events", group: "Content & trust" },
  { label: "Notifications", to: "/administrator/notifications", group: "Messaging" },
  {
    label: "Calibration",
    to: "/administrator/calibration",
    activeMatch: isCalibrationAdminRoute,
    group: "Governance",
  },
  { label: "Audit trail", to: "/administrator/audit-trail", group: "Records" },
  {
    label: "Settings",
    to: "/administrator/settings",
    activePaths: [
      "/administrator/settings",
      "/administrator/menu-builder",
      "/administrator/pages",
      "/administrator/domains",
    ],
    group: "Configuration",
  },
];

// The prototype's grouped nav has no "Content" entry at all — dropped from
// the visible sidebar to match it exactly, and (per the Content->Moderation
// merge) /administrator/content is now a <Navigate> redirect to
// "../moderation?tab=reports" (see index.jsx), not a rendering route, so
// there is no longer a real page here that would need a fallback header.
//
// Icon-font glyph for each nav item, matched against the prototype's own
// class name (icon-tachometer-alt-solid, icon-shield-alt-solid, etc.) —
// see AdminShell.module.css for how these codepoints were decoded.
// Settings has no font glyph in the prototype (it renders an inline SVG
// there), so it falls back to the closest react-icons equivalent.
const NAV_ICON_CLASS = {
  Home: shellStyles.navIconHome,
  Members: shellStyles.navIconMembers,
  Structure: shellStyles.navIconStructure,
  Moderation: shellStyles.navIconShield,
  Events: shellStyles.navIconEvents,
  Notifications: shellStyles.navIconNotifications,
  Calibration: shellStyles.navIconCalibration,
  "Audit trail": shellStyles.navIconAuditTrail,
};

function NavIcon({ label }) {
  if (label === "Settings") {
    return <FiSettings aria-hidden="true" />;
  }
  const iconClass = NAV_ICON_CLASS[label];
  if (!iconClass) return null;
  return <i className={`${shellStyles.navIcon} ${iconClass}`} aria-hidden="true" />;
}

function navItemTestId(label) {
  return `clan-admin-nav-${label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")}`;
}

// Eyebrow copy mirrors the nav GROUP each page belongs to — the one
// eyebrow/group pairing verified against the live prototype (Home's
// eyebrow reads "Overview", exactly its nav group) — extended to the rest
// of the sections on that same, confirmed pattern. Description copy is
// carried over from the pre-redesign shell and not yet re-verified
// page-by-page; future phase agents redesigning each page body should
// double check it against the prototype for their section.
const NAV_META = {
  Home: {
    eyebrow: "Overview",
    description: "Live clan operations, configuration health, and recent administration activity.",
  },
  Members: {
    eyebrow: "People",
    description: "Member access, validation, and user management.",
  },
  // Eyebrow/description verified directly against the live prototype's
  // Structure page (adm-eyebrow reads "Structure", not the nav group
  // "People" — same pattern as Home reading "Overview" rather than its
  // own group). adm-sub copy carried over verbatim.
  Structure: {
    eyebrow: "Structure",
    description:
      "Build the shape of your clan — the teams, depots and functions people belong to. Create subclans and manage who leads them, right here.",
  },
  // Eyebrow/description verified directly against the live prototype's
  // Moderation page (adm-eyebrow reads "Trust & safety", not the nav group
  // "Content & trust" — same pattern as Structure/Events above). adm-sub
  // copy carried over verbatim.
  Moderation: {
    eyebrow: "Trust & safety",
    description:
      "Reports, sign-up reviews and auto-flagged media in one queue. Triage in bulk, or open any item for the full context before you decide.",
  },
  // Eyebrow/description verified directly against the live prototype's
  // Events page (adm-eyebrow reads "Programming", not the nav group
  // "Content & trust" — same pattern as Structure above). adm-sub copy
  // carried over verbatim.
  Events: {
    eyebrow: "Programming",
    description:
      "Schedules, recordings and invitations together — set up an event and its recording and RSVP rules in one place.",
  },
  Notifications: {
    eyebrow: "Messaging",
    description: "Notification rules, channels, and delivery settings.",
  },
  Calibration: {
    eyebrow: "Governance",
    description:
      "AI oversight: audit log, calibration roles, cron health, and report exports.",
  },
  // Prototype copy (getComputedStyle/DOM read on
  // https://uniflo-prototype-staging.up.railway.app/#admin, RECORDS > Audit
  // trail): adm-eyebrow "Evidence", adm-sub carried over verbatim.
  "Audit trail": {
    eyebrow: "Evidence",
    description:
      "Every consequential action in the clan, filterable and inspectable — with the filters you've applied shown up front.",
  },
  // Description verified directly against the live prototype's Settings
  // hub subtitle (adm-sub, exact copy incl. the em dash) — it's the same
  // shared header used for every ?tab= inside Settings too (there's no
  // per-tab override), same pattern as every other section here.
  Settings: {
    eyebrow: "Configuration",
    description:
      "Everything that configures the clan, grouped by what it touches. Open an area to change it — one save bar covers the lot.",
  },
};

function displayNameFromUser(userInfo = {}) {
  return (
    `${userInfo?.first_name || userInfo?.firstName || ""} ${
      userInfo?.last_name || userInfo?.lastName || ""
    }`.trim() ||
    userInfo?.name ||
    userInfo?.username ||
    userInfo?.email ||
    "Member"
  );
}

function dashboardGreeting(userInfo) {
  const firstName = displayNameFromUser(userInfo).split(/\s+/)[0] || "Member";
  return `Good morning, ${firstName}`;
}

function isNotificationsRoute(pathname) {
  const normalizedPath = normalizeAdminPath(pathname);
  return (
    normalizedPath === "/administrator/notifications" ||
    normalizedPath.startsWith("/administrator/notifications/")
  );
}

function buildTenantAdminNav(canViewNotificationsAdmin) {
  if (canViewNotificationsAdmin) {
    return ADMIN_SECTIONS;
  }

  return ADMIN_SECTIONS.filter((item) => item.label !== "Notifications");
}

function buildNotificationOnlyNav() {
  return [
    { label: "Notifications", to: "/administrator/notifications", group: "Messaging" },
  ];
}

export default function AdminLayout() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => ({ user: state.user }));
  const navigate = useNavigate();
  const location = useLocation();
  // Structure (Subclans) page's "+ New subclan" header button. The page
  // owns the click behaviour (opens its own create-subclan modal) and just
  // hands the shell a descriptor to render in the shared page header,
  // matching the prototype's adm-head__actions button. Settings used to
  // register an equivalent `settingsHeaderAction` on this same mechanism
  // for its cross-form "Save Settings" button; that has been retired in
  // favour of the global save bar below (see saveHandlers/saveBar) — this
  // registry itself survives unrelated for Subclans/Events, which still use
  // it for plain "+ New" buttons, not save/dirty tracking.
  const [subclansHeaderAction, setSubclansHeaderAction] = useState(null);
  // Events page's "+ New event" header button. Same registration pattern
  // as subclansHeaderAction above — the page owns the click behaviour
  // (opens its own create-event modal) and just hands the shell a
  // descriptor to render in the shared page header, matching the
  // prototype's adm-head__actions button on the Events section.
  const [eventsHeaderAction, setEventsHeaderAction] = useState(null);
  // Global save bar (08-admin-entry.jsx:36,42,46-48): a Set of opaque
  // per-field/per-panel keys, exactly like the prototype's `changes` — its
  // size is the "N unsaved changes" count the bar renders. Handed to
  // routed pages as `saveBar` on the existing Outlet context below (the
  // same handoff already used for setSubclansHeaderAction etc.), so a
  // page adopts it with
  // `const { saveBar } = useOutletContext(); saveBar.bump("panel.field")`.
  // Settings.jsx is the first (and, today, only) adopter — see its
  // `saveBar.bump("settings")`/`saveBar.clear("settings")`/
  // `saveBar.registerHandlers(...)` wiring.
  const [changes, setChanges] = useState(() => new Set());
  const dirty = changes.size;

  const bump = useCallback((key) => {
    setChanges((current) => {
      if (current.has(key)) return current;
      const next = new Set(current);
      next.add(key);
      return next;
    });
  }, []);

  // Symmetric counterpart to bump() — lets a page remove its own key once
  // it's no longer dirty (a successful save, a discard, or an out-of-band
  // reload that resets the page's local dirty flag). Additive: nothing
  // before Settings.jsx's migration ever called this, and bump()'s own
  // add-only behaviour/callers are unaffected by its existence.
  const clear = useCallback((key) => {
    setChanges((current) => {
      if (!current.has(key)) return current;
      const next = new Set(current);
      next.delete(key);
      return next;
    });
  }, []);

  // Lets the active routed page register real save/discard functions so
  // the bar's own buttons trigger that page's actual persistence/restore
  // logic instead of only clearing this Set. `null` (the default, and what
  // every page except Settings.jsx leaves it) preserves the original
  // Stage-1 fallback behaviour exactly — see the "no handler registered"
  // tests in AdminLayout.savebar.test.jsx, which still pass unmodified.
  const [saveHandlers, setSaveHandlers] = useState(null);

  // Mirrors the prototype's discard() (08-admin-entry.jsx:47): no
  // confirmation prompt, matching that source precisely. Reuses the
  // app-wide react-hot-toast host already mounted in App.js (the same one
  // Settings.jsx's own save flow already calls) instead of a second toast
  // system. When a page has registered a real onDiscard (Settings.jsx),
  // it runs first so the page's own state is actually restored before this
  // clears the shell-level dirty tracking — both fire unconditionally
  // (discard, unlike save, cannot fail asynchronously), so the fallback
  // path for pages that never register a handler is byte-identical to
  // before.
  const discardChanges = useCallback(() => {
    saveHandlers?.onDiscard?.();
    setChanges(new Set());
    toast.success("Changes discarded");
  }, [saveHandlers]);

  // Mirrors the prototype's save() (08-admin-entry.jsx:48) ONLY when no
  // page has registered a real save — that fallback path (clear + generic
  // "N changes saved" toast) is exactly the original Stage-1 behaviour,
  // unchanged. When a page HAS registered onSave (Settings.jsx), delegate
  // to it and return early instead: saving is asynchronous and can fail,
  // so this bar must not clear itself (or claim success via toast) until
  // the page's own save actually succeeds — the page is responsible for
  // calling `saveBar.clear(key)` itself once it knows the real result
  // (Settings.jsx does this via a `dirty`-mirroring effect, so a failed
  // save leaves the bar showing and re-clickable, exactly like the old
  // header Save button did via its own disabled/dirty gate).
  const saveChanges = useCallback(() => {
    if (saveHandlers?.onSave) {
      saveHandlers.onSave();
      return;
    }
    setChanges(new Set());
    toast.success(`${dirty} ${dirty === 1 ? "change" : "changes"} saved`);
  }, [dirty, saveHandlers]);

  // Memoized deliberately: this object is hung on the Outlet context below,
  // and Settings.jsx depends on it (by reference) in two effects' own dep
  // arrays. Without memoizing, a fresh `{ bump, dirty, clear,
  // registerHandlers }` literal on every AdminLayout render would change
  // identity on every render for ANY reason (including the registerHandlers
  // effect's own setSaveHandlers call), which would re-trigger that same
  // effect, which calls registerHandlers again with a new payload object,
  // which changes saveHandlers again — an infinite render loop. Keying the
  // memo on bump/dirty/clear (all stable except `dirty`, which only changes
  // when `changes` genuinely does) breaks that cycle: registering a handler
  // no longer perturbs this object's identity at all.
  const saveBar = useMemo(
    () => ({ bump, dirty, clear, registerHandlers: setSaveHandlers }),
    [bump, dirty, clear]
  );

  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [notificationAccess, setNotificationAccess] = useState(null);
  // "unknown" answer for the notifications-access request, tracked
  // separately from `notificationAccess` itself (which stays whatever it
  // last resolved to). The redirect effect below needs to tell "we don't
  // know yet" apart from "we asked and the answer was no" — collapsing
  // both into notificationAccess === null (as before) is exactly the race
  // that let a tenant owner/admin get bounced off /administrator/notifications
  // before their own access response had come back. "error" means the
  // request itself failed/rejected — see hasResolvedAccessRef's own
  // comment on rule 17: a transient failure must not read as "denied" for
  // an already-confirmed tenant admin/owner.
  const [notificationAccessStatus, setNotificationAccessStatus] = useState("pending");
  const [tenantAdminEnabled, setTenantAdminEnabled] = useState(false);
  const [tenantAdminRole, setTenantAdminRole] = useState("");
  const [accessLoading, setAccessLoading] = useState(true);
  const [bootstrapUserInfo, setBootstrapUserInfo] = useState(null);
  // Tracks whether we have ever resolved access (bootstrap returned
  // successfully) for this user/tenant. The redirect effect uses this
  // as a precondition before bouncing the user — so transient
  // network failures during a re-fetch never collapse a
  // previously-resolved admin verdict and bounce the user to /home
  // mid-action. See work contract rule 17 in AGENTS.md.
  const hasResolvedAccessRef = useRef(false);
  const {
    siteName,
    platformSiteName,
    logoUrl,
    tenant,
    tenantPreviewSlug,
  } = useBranding();
  const tenantPath = useCallback(
    (path) => buildTenantRelativePath(path, tenantPreviewSlug),
    [tenantPreviewSlug]
  );

  const reduxUserInfo = user?.userinfo || null;
  const userInfo = reduxUserInfo || bootstrapUserInfo;
  const platformAdminEnabled =
    userInfo?.accountRole === "admin" || userInfo?.role === "admin";
  const adminBrandName = (platformAdminEnabled ? platformSiteName : siteName) || "Uniflo";
  const notificationsRoute = isNotificationsRoute(location.pathname);
  const canViewNotificationsAdmin =
    platformAdminEnabled || notificationAccess?.canViewNotificationsAdmin === true;
  const normalizedAdminPath = normalizeAdminPath(location.pathname);
  const isDashboardRoute = normalizedAdminPath === "/administrator";
  const isSubclansRoute = normalizedAdminPath === "/administrator/subclans";
  const isEventsRoute = normalizedAdminPath === "/administrator/events";

  const navItems = useMemo(() => {
    // Platform admins always resolve canViewNotificationsAdmin === true
    // (see above), so both branches already produced the identical list —
    // collapsed into one call instead of two duplicate nav arrays.
    if (platformAdminEnabled || tenantAdminEnabled) {
      return buildTenantAdminNav(canViewNotificationsAdmin);
    }
    return buildNotificationOnlyNav();
  }, [canViewNotificationsAdmin, platformAdminEnabled, tenantAdminEnabled]);

  // Moderation nav-item badge count. Reuses the exact same hook/endpoint
  // Dashboard.jsx already calls for its own "needs your attention" card
  // (useModerationQueue({status:"needs_review"}), reading .data.total) —
  // react-query dedupes identical query keys across components, so mounting
  // it here too does not add a second poll of the endpoint, only another
  // subscriber to the same cached query. Gated on the same condition that
  // decides whether "Moderation" is even in navItems (only tenant/platform
  // admins ever see that nav entry) so a notifications-only admin — who can
  // legitimately sit on /administrator/notifications for their whole
  // session — never fires a request against an endpoint their role is not
  // authorized for (requireTenantAdminOrPlatformAdmin server-side).
  const canSeeModerationBadge = platformAdminEnabled || tenantAdminEnabled;
  const moderationQueueQuery = useModerationQueue({
    status: "needs_review",
    enabled: canSeeModerationBadge,
  });
  const moderationBadgeCount = canSeeModerationBadge
    ? moderationQueueQuery.data?.data?.total ?? null
    : null;

  const settingsTabActiveKey = useMemo(() => {
    if (
      normalizedAdminPath === "/administrator/menu-builder" ||
      normalizedAdminPath.startsWith("/administrator/menu-builder/")
    ) {
      return "menu-builder";
    }

    if (
      normalizedAdminPath === "/administrator/pages" ||
      normalizedAdminPath.startsWith("/administrator/pages/")
    ) {
      return "custom-pages";
    }

    // Domains used to redirect into Settings' "events" (Kits) tab, so mapping
    // it to that key was right. It is now a first-class route rendering
    // <Domains/> directly, and returning "events" made the shell label the page
    // "Kits" above Domains' own header.
    //
    // Returning null renders no settings tab strip here, which is correct for a
    // standalone route. Note its Public-site siblings (menu-builder,
    // custom-pages) do show the strip, so there is an open question about
    // whether Domains should too — answering it means giving Domains a real tab
    // key, and TAB_ALIASES currently maps `domains` -> `events`, so that key
    // would be shadowed by its own alias. That is a deep-link contract change
    // and wants doing deliberately, not as a side effect of this fix.
    if (
      normalizedAdminPath === "/administrator/domains" ||
      normalizedAdminPath.startsWith("/administrator/domains/")
    ) {
      return null;
    }

    if (
      normalizedAdminPath === "/administrator/settings" ||
      normalizedAdminPath.startsWith("/administrator/settings/")
    ) {
      // No ?tab= -> the grouped-card Settings hub (Settings.jsx's own
      // "hub" state). Previously defaulted to "general" here, which kept
      // this pill strip (and the "General" page title below) showing on
      // what's supposed to be the landing page — the prototype's hub has
      // no tab strip at all, just its search box. A real ?tab=<key> still
      // resolves exactly as before.
      return normalizeAdminSettingsTabKey(
        new URLSearchParams(location.search).get("tab")
      );
    }

    return null;
  }, [location.search, normalizedAdminPath]);

  useEffect(() => {
    let active = true;
    const api = axios.create({ withCredentials: true });

    const loadAccess = async () => {
      setAccessLoading(true);
      setNotificationAccessStatus("pending");
      try {
        const notificationAccessRequest = api
          .get(apiUrl("/api/v1/admin/notifications/access"))
          .catch(() => null);
        const applyNotificationAccess = async () => {
          const notificationResponse = await notificationAccessRequest;
          if (!active) return;
          if (notificationResponse) {
            setNotificationAccess(notificationResponse?.data?.data || null);
            setNotificationAccessStatus("resolved");
          } else {
            // The request itself failed/rejected (already swallowed to
            // null above) — mark it "error" rather than "resolved", so
            // the redirect effect knows not to treat this as a genuine
            // denial (rule 17: never bounce an admin on a transient
            // failure).
            setNotificationAccessStatus("error");
          }
        };
        const bootstrapResponse = await api.get(apiUrl("/api/v1/users/bootstrap"));

        if (!active) return;

        const bootstrap = bootstrapResponse.data?.data || {};
        const restoredUser = bootstrap.user || reduxUserInfo || null;
        if (restoredUser) {
          setBootstrapUserInfo(restoredUser);
          if (!reduxUserInfo) {
            dispatch(updateUserInfo(restoredUser));
          }
        }
        const restoredPlatformAdmin =
          restoredUser?.accountRole === "admin" || restoredUser?.role === "admin";
        if (restoredPlatformAdmin) {
          setTenantAdminEnabled(false);
          setTenantAdminRole("");
          setNotificationAccess({
            isAdmin: true,
            canViewNotificationsAdmin: true,
            canManageConfig: true,
            canSendBulkNotifications: true,
          });
          setNotificationAccessStatus("resolved");
          hasResolvedAccessRef.current = true;
          return;
        }

        const memberships = Array.isArray(bootstrap.tenantMemberships)
          ? bootstrap.tenantMemberships
          : [];
        const currentTenantId = bootstrap.currentTenant?.id || tenant?.id || null;
        const currentTenantSlug =
          bootstrap.currentTenant?.slug || tenant?.slug || tenantPreviewSlug || "";

        const membership = memberships.find((item) => {
          const membershipTenant = item?.tenant || {};
          return (
            (currentTenantId && membershipTenant.id === currentTenantId) ||
            (currentTenantSlug && membershipTenant.slug === currentTenantSlug)
          );
        });

        const role = `${membership?.role || ""}`.trim();
        const hasTenantAdminAccess =
          role === "tenant_owner" || role === "tenant_admin";
        setTenantAdminEnabled(hasTenantAdminAccess);
        setTenantAdminRole(role);
        hasResolvedAccessRef.current = true;
        if (hasTenantAdminAccess) {
          // Deliberately NOT awaited: the notifications-access answer must
          // not hold up clearing accessLoading below (a slow endpoint would
          // otherwise strand this owner/admin on the "Checking your
          // administrator access..." screen for no reason — they already
          // have tenant-admin access to every OTHER section). The race this
          // used to cause — the redirect effect firing while this promise
          // was still in flight, reading a stale "denied" from
          // canViewNotificationsAdmin — is fixed by notificationAccessStatus
          // above, which the redirect effect awaits instead of inferring
          // "resolved" from accessLoading's own timing.
          applyNotificationAccess();
          return;
        }
        if (notificationsRoute) {
          await applyNotificationAccess();
        } else {
          setNotificationAccess(null);
          setNotificationAccessStatus("resolved");
        }
      } catch (_error) {
        if (!active) return;
        if (!hasResolvedAccessRef.current && _error?.response?.status === 401) {
          navigate(tenantPath("/login"), { replace: true });
          return;
        }
        // Transient bootstrap/notification fetch failures must NOT
        // collapse a previously-resolved tenantAdmin verdict back to
        // false. If we ever resolved access successfully, keep the
        // last-known role; otherwise leave the initial defaults.
        // The redirect effect uses `hasResolvedAccessRef` to decide
        // whether to act on `tenantAdminEnabled`, so a transient
        // error here will not bounce the admin to /home.
      } finally {
        if (active) {
          setAccessLoading(false);
        }
      }
    };

    loadAccess();

    return () => {
      active = false;
    };
  }, [
    dispatch,
    navigate,
    notificationsRoute,
    reduxUserInfo,
    tenant?.id,
    tenant?.slug,
    tenantPath,
    tenantPreviewSlug,
  ]);

  useEffect(() => {
    if (!userInfo || accessLoading) {
      return;
    }

    if (platformAdminEnabled) {
      return;
    }

    if (tenantAdminEnabled) {
      if (notificationsRoute) {
        // "unknown" is its own state here, not inferred from
        // accessLoading's timing: a tenant owner/admin's notifications
        // access is fetched in parallel with (and, for this branch, not
        // awaited alongside) the bootstrap call, so accessLoading can flip
        // false well before the access verdict itself arrives. Bouncing on
        // canViewNotificationsAdmin === false at that instant is exactly
        // the bug this guards against — wait for a real answer first.
        if (notificationAccessStatus === "pending") {
          return;
        }
        // A failed/rejected access request ("error") must not read as a
        // denial for an already-confirmed tenant admin/owner — rule 17,
        // same guard hasResolvedAccessRef applies further down for the
        // member path.
        if (notificationAccessStatus === "resolved" && !canViewNotificationsAdmin) {
          navigate(tenantPath("/administrator"), { replace: true });
        }
        return;
      }

      if (!isTenantAdminRoute(location.pathname)) {
        navigate(tenantPath("/administrator"), { replace: true });
      }
      return;
    }

    if (notificationsRoute && canViewNotificationsAdmin) {
      return;
    }

    // Never bounce the user to /home unless we've actually resolved
    // their access status at least once. Without this guard a
    // transient bootstrap fetch error (or a race on first paint
    // before the bootstrap response arrives) would silently kick a
    // tenant admin out of the admin pages mid-action. See work
    // contract rule 17 in AGENTS.md.
    if (!hasResolvedAccessRef.current) {
      return;
    }

    navigate(tenantPath("/home"), { replace: true });
  }, [
    accessLoading,
    canViewNotificationsAdmin,
    location.pathname,
    navigate,
    notificationAccessStatus,
    notificationsRoute,
    platformAdminEnabled,
    tenantAdminEnabled,
    tenantPath,
    userInfo,
  ]);

  useEffect(() => {
    if (!isSubclansRoute) {
      setSubclansHeaderAction(null);
    }
  }, [isSubclansRoute]);

  useEffect(() => {
    if (!isEventsRoute) {
      setEventsHeaderAction(null);
    }
  }, [isEventsRoute]);

  if (!userInfo || accessLoading) {
    return (
      <div className={styles.adminShell}>
        <div className={styles.accessDenied}>
          <div className={styles.accessDeniedTitle}>Loading...</div>
          <div className={styles.accessDeniedText}>
            Checking your administrator access.
          </div>
        </div>
      </div>
    );
  }

  if (
    !platformAdminEnabled &&
    !tenantAdminEnabled &&
    !canViewNotificationsAdmin
  ) {
    return (
      <div className={styles.adminShell}>
        <div className={styles.accessDenied}>
          <div className={styles.accessDeniedTitle}>Access Denied</div>
          <div className={styles.accessDeniedText}>
            You do not have permission to view this page.
          </div>
          <Link to={tenantPath("/home")} className={styles.backLink}>
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const activeNavItem =
    navItems.find((item) => isAdminNavItemActive(item, location.pathname)) ||
    navItems[0];
  const activeNavMeta = NAV_META[activeNavItem?.label] || NAV_META.Home;
  const railBrandTitle = "Administration";
  const railBrandSubtitle = tenant?.name || adminBrandName;
  const roleLabel = platformAdminEnabled
    ? "Platform owner"
    : tenantAdminRole === "tenant_owner"
      ? "Owner"
      : tenantAdminRole === "tenant_admin"
        ? "Clan admin"
        : canViewNotificationsAdmin
          ? "Notifications admin"
          : "Member";
  const settingsTabLabel =
    ADMIN_SETTINGS_TABS.find(
      (tab) => tab.key === settingsTabActiveKey
    )?.label || null;
  const heroEyebrow = isDashboardRoute ? "Overview" : activeNavMeta.eyebrow;
  const heroDescription = isDashboardRoute
    ? "Everything that needs a decision, in one place — then the numbers behind your clan."
    : activeNavMeta.description;
  const hasTenantContext = Boolean(tenant?.id || tenant?.slug || tenantPreviewSlug);
  const canInviteMembers =
    isDashboardRoute && (tenantAdminEnabled || (platformAdminEnabled && hasTenantContext));
  // The prototype's <h1> reads "Subclans" on the Structure page — the nav
  // item itself is labelled "Structure" (matches its sidebar entry), same
  // split as Home's nav label vs. its "Good morning, X" greeting title.
  const pageTitle = isDashboardRoute
    ? dashboardGreeting(userInfo)
    : isSubclansRoute
      ? "Subclans"
      : settingsTabLabel && activeNavItem?.label === "Settings"
        ? settingsTabLabel
        : activeNavItem?.label || "Home";

  // Nav-item badge counts, keyed by label. Moderation is the only entry
  // wired today (real data via moderationBadgeCount above, prototype:
  // 08-admin-entry.jsx:24 — tone "warn"). Members/pending-signups is a
  // separate, not-yet-built data source (out of scope here — needs its own
  // pending-signups count on the Members surface) so it deliberately has no
  // entry, matching the house rule below: no real count, no badge.
  const NAV_BADGE_COUNTS = { Moderation: moderationBadgeCount };

  // Grouped by nav "group" in ADMIN_SECTIONS/HIDDEN order, matching the
  // prototype's OVERVIEW / PEOPLE / CONTENT & TRUST / MESSAGING /
  // GOVERNANCE / RECORDS / CONFIGURATION sections exactly. An item without
  // real backing data for its badge count (e.g. pending members, queue
  // size) simply renders without one — never a fabricated number.
  const navGroups = [];
  const navGroupIndex = new Map();
  navItems.forEach((item) => {
    const groupLabel = item.group || "";
    if (!navGroupIndex.has(groupLabel)) {
      navGroupIndex.set(groupLabel, navGroups.length);
      navGroups.push({ label: groupLabel, items: [] });
    }
    navGroups[navGroupIndex.get(groupLabel)].items.push(item);
  });

  const adminSecondaryContent = (
    // `.tokens` (AdminShell.module.css) defines every --adm-* custom
    // property this rail's classes read (e.g. .navItemActive's
    // background: var(--adm-soft-brand)). AdminLayout only applies it to
    // <main data-testid="clan-admin-canvas"> below, but this rail renders
    // through ClanWorkspaceShell's `secondaryContent` prop into a sibling
    // <aside>, outside that element's subtree — so without repeating
    // `.tokens` here, every --adm-* read in the rail resolves to nothing.
    <div className={`${shellStyles.rail} ${shellStyles.tokens}`}>
      <div className={shellStyles.railBrand} data-testid="clan-admin-rail-brand">
        <span className={shellStyles.railShield} aria-hidden="true">
          <i className={`${shellStyles.navIcon} ${shellStyles.navIconShield}`} aria-hidden="true" />
        </span>
        <div className={shellStyles.railBrandText}>
          <div className={shellStyles.railTitle}>{railBrandTitle}</div>
          <div className={shellStyles.railClan}>{railBrandSubtitle}</div>
        </div>
      </div>

      <nav
        className={shellStyles.railNav}
        aria-label="Clan admin navigation"
        data-testid="clan-admin-nav"
      >
        {navGroups.map((group) => (
          <div className={shellStyles.navGroup} key={group.label || "ungrouped"}>
            {group.label ? (
              <div className={shellStyles.navGroupLabel}>{group.label}</div>
            ) : null}
            {group.items.map((item) => {
              const isActive = isAdminNavItemActive(item, location.pathname);
              const href = tenantPath(item.to);
              const badgeCount = NAV_BADGE_COUNTS[item.label];
              const showBadge = typeof badgeCount === "number" && badgeCount > 0;

              return (
                <Link
                  key={item.to}
                  to={href}
                  className={`${shellStyles.navItem} ${
                    isActive ? shellStyles.navItemActive : ""
                  }`.trim()}
                  aria-current={isActive ? "page" : undefined}
                  aria-label={`Open ${item.label} admin section`}
                  data-testid={navItemTestId(item.label)}
                >
                  <span className={shellStyles.navItemIcon}>
                    <NavIcon label={item.label} />
                  </span>
                  <span className={shellStyles.navItemLabel}>{item.label}</span>
                  {showBadge ? (
                    <span
                      className={`${shellStyles.navItemBadge} ${shellStyles.navItemBadgeWarn}`}
                      data-testid={`${navItemTestId(item.label)}-badge`}
                    >
                      {badgeCount}
                    </span>
                  ) : null}
                </Link>
              );
            })}
          </div>
        ))}
      </nav>

      <div className={shellStyles.railFoot}>
        <Link
          to={tenantPath("/home")}
          className={shellStyles.railWorkspaceLink}
          data-testid="clan-admin-view-workspace"
        >
          <i className={`${shellStyles.icon} ${shellStyles.iconArrowTopRight}`} aria-hidden="true" />
          View workspace
        </Link>
        <div className={shellStyles.railRole} data-testid="clan-admin-role">
          <span className={shellStyles.railRoleDot} aria-hidden="true" />
          <span>
            Signed in as <b>{roleLabel}</b>
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <ClanWorkspaceShell
      user={userInfo}
      siteName={tenant?.name || adminBrandName}
      secondaryLabel="Clan admin navigation"
      secondaryContent={adminSecondaryContent}
      secondaryVariant="solid"
      secondaryWidth="278px"
      shellVariant="clan-redesign"
      testId="clan-admin-shell"
    >
      <>
        {/*
          Inline `style` below, not a CSS-module edit: reserves the
          prototype's fixed 92px bottom gap for the global save bar
          rendered further down (proto-admin.html:3535, `.adm-canvas {
          padding-bottom: 92px }`) unconditionally, so content never jumps
          when the bar toggles. Wins over AdminLayout.module.css's
          72px/50px/42px `.adminCanvas` padding-bottom at every breakpoint
          by ordinary CSS precedence (inline style > stylesheet class).

          paddingRight reserves the WorkspacePeopleRail's footprint (fixed,
          80px wide, 16px off the viewport edge = 96px) so canvas content
          (e.g. Dashboard's 4-column stat-tile row, which has no max-width
          and would otherwise stretch under the rail) never renders
          underneath it — AdminLayout.module.css's `.adminCanvas` has no
          right-side reservation of its own, unlike clanRedesign's own
          `.homeFrame { padding-right: 96px }` for this exact component.
          Reads the --adm-people-rail-gutter custom property (AdminShell
          .module.css's `.tokens`, already applied to this element) instead
          of a literal number so it can zero out at the same <=1180px
          breakpoint where the rail hides itself — inline style still wins
          the cascade the same way paddingBottom above does; only the value
          it resolves to is responsive.
        */}
        <main
          className={`${styles.adminCanvas} ${shellStyles.tokens}`}
          style={{ paddingBottom: 92, paddingRight: "var(--adm-people-rail-gutter, 96px)" }}
          data-testid="clan-admin-canvas"
        >
          <header className={shellStyles.pageHeader} data-testid="clan-admin-page-header">
            <div className={shellStyles.pageHeaderText}>
              <div className={shellStyles.pageEyebrow}>{heroEyebrow}</div>
              <h1 className={shellStyles.pageTitle}>{pageTitle}</h1>
              <p className={shellStyles.pageSub}>{heroDescription}</p>
            </div>
            <div className={shellStyles.pageHeaderActions}>
              {canInviteMembers ? (
                <button
                  className={shellStyles.primaryButton}
                  type="button"
                  onClick={() => setInviteDialogOpen(true)}
                  data-testid="clan-admin-invite-members"
                >
                  <i className={`${shellStyles.icon} ${shellStyles.iconUserFriends}`} aria-hidden="true" />
                  Invite members
                </button>
              ) : null}
              {isSubclansRoute && subclansHeaderAction ? (
                <button
                  className={shellStyles.primaryButton}
                  type="button"
                  onClick={subclansHeaderAction.onClick}
                  disabled={subclansHeaderAction.disabled}
                  data-testid="clan-admin-new-subclan"
                >
                  <i className={`${shellStyles.icon} ${shellStyles.iconPlus}`} aria-hidden="true" />
                  {subclansHeaderAction.label || "New subclan"}
                </button>
              ) : null}
              {isEventsRoute && eventsHeaderAction ? (
                <button
                  className={shellStyles.primaryButton}
                  type="button"
                  onClick={eventsHeaderAction.onClick}
                  disabled={eventsHeaderAction.disabled}
                  data-testid="clan-admin-new-event"
                >
                  <i className={`${shellStyles.icon} ${shellStyles.iconPlus}`} aria-hidden="true" />
                  {eventsHeaderAction.label || "New event"}
                </button>
              ) : null}
            </div>
          </header>
          {settingsTabActiveKey ? (
            <AdminSettingsTabs
              activeKey={settingsTabActiveKey}
              buildHref={tenantPath}
            />
          ) : null}
          <div className={styles.adminOutlet}>
            <Outlet
              context={{
                notificationAccess,
                adminAccess: {
                  isPlatformAdmin: platformAdminEnabled,
                  isTenantAdmin: tenantAdminEnabled,
                  tenantAdminRole,
                },
                setSubclansHeaderAction,
                setEventsHeaderAction,
                // Shell-level dirty-tracking + save/discard-delegation API —
                // see the memoized `saveBar` above. Settings.jsx is the
                // first adopter: `saveBar.bump("settings")`/
                // `saveBar.clear("settings")` mirror its own local `dirty`
                // boolean, and `saveBar.registerHandlers({ onSave,
                // onDiscard })` points the bar's buttons at Settings' real
                // save/discard logic instead of the generic clear+toast
                // fallback below.
                saveBar,
              }}
            />
          </div>
        </main>
        {dirty > 0 ? (
          // Global save bar — the prototype's ONE mechanism replacing the
          // legacy header cross-form Save (08-admin-entry.jsx:86-95).
          // Settings.jsx is migrated onto it fully now: its own header
          // Save button and hidden form are retired, and this bar's
          // Save/Discard buttons call back into Settings' real
          // save/discard logic via saveHandlers (see saveChanges/
          // discardChanges above). Subclans/Events still render their own,
          // unrelated "+ New" header buttons via subclansHeaderAction/
          // eventsHeaderAction — untouched by this migration.
          <div
            className={`${shellStyles.saveBar} ${shellStyles.tokens}`}
            role="status"
            data-testid="clan-admin-savebar"
          >
            <span className={shellStyles.saveBarDot} aria-hidden="true" />
            <span
              className={shellStyles.saveBarText}
              data-testid="clan-admin-savebar-count"
            >
              <b>{dirty}</b> unsaved {dirty === 1 ? "change" : "changes"}
            </span>
            <div className={shellStyles.saveBarActions}>
              <button
                type="button"
                className={shellStyles.saveBarDiscard}
                onClick={discardChanges}
                data-testid="clan-admin-savebar-discard"
              >
                Discard
              </button>
              <button
                type="button"
                className={shellStyles.saveBarSave}
                onClick={saveChanges}
                disabled={Boolean(saveHandlers?.disabled)}
                data-testid="clan-admin-savebar-save"
              >
                Save changes
              </button>
            </div>
          </div>
        ) : null}
        {/*
          The prototype's right-hand people rail + invite FAB
          (proto-admin.html:1936-1983, `.people-rail`/`.people-invite`) —
          already shipped on ~11 other clan-redesign surfaces as one shared,
          data-backed component (real GET /api/v1/workspace/members/search,
          real MiniChat, real InviteDialog). It is fixed-position and
          mounted directly, NOT through ClanWorkspaceShell's
          `secondaryContent` slot (already taken by the left nav rail
          above) — so it needs zero shell changes and cannot contend with
          the nav. Wired to the SAME inviteDialogOpen state / <InviteDialog>
          already mounted below, not a second dialog instance.
        */}
        <WorkspacePeopleRail
          clanName={tenant?.name || adminBrandName}
          clanLogoUrl={logoUrl}
          tenantSlug={tenantPreviewSlug}
          user={userInfo}
          onInvite={() => setInviteDialogOpen(true)}
        />
        <InviteDialog
          open={inviteDialogOpen}
          onClose={() => setInviteDialogOpen(false)}
        />
      </>
    </ClanWorkspaceShell>
  );
}
